<template>
  <!-- Calque full-bleed sous le contenu -->
  <div class="pointer-events-none absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[100dvw] -z-10" aria-hidden="true">
    <!-- grille douce -->
    <div v-if="showGrid" class="absolute inset-0 bg-grid" :style="{ opacity: gridOpacity }"></div>

    <!-- pattern tuile -->
    <div
      v-if="showTiledPattern"
      class="absolute inset-0 bg-mail-pattern"
      :style="{ '--mailOpacity': String(patternOpacity), '--tile': tileSize + 'px' }"
    ></div>

    <!-- field papier -->
    <div
      v-if="showPaperField"
      class="absolute inset-0 mail-field"
      :style="{ '--mailOpacity': String(paperOpacity), '--tile': tileSize + 'px' }"
    ></div>

    <!-- motifs aléatoires (pilotés par allowedKinds / kinds / weights) -->
    <svg
      v-for="m in motifs"
      :key="m.id"
      class="absolute"
      :style="{
        left: m.x + '%',
        top: m.y + '%',
        width: m.size + 'px',
        height: m.size + 'px',
        transform: `translate(-50%,-50%) rotate(${m.rot}deg)`,
        opacity: m.opacity,
        color: m.color
      }"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      :stroke-width="m.stroke"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <!-- ===== MAIL SET ===== -->
      <template v-if="m.kind === 'mail'">
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </template>

      <template v-else-if="m.kind === 'plane'">
        <path d="M22 2L11 13" />
        <path d="M22 2l-7 20-4-9-9-4 20-7z" />
      </template>

      <template v-else-if="m.kind === 'chat'">
        <rect x="3" y="4" width="18" height="12" rx="3" />
        <path d="M8 20l3-4" />
      </template>

      <template v-else-if="m.kind === 'at'">
        <path d="M16 12a4 4 0 1 1-2.5-3.7" />
        <path d="M16 12v2a2 2 0 0 0 4 0v-1a6 6 0 1 0-3 5" />
      </template>

      <template v-else-if="m.kind === 'dot'">
        <circle cx="12" cy="12" r="2" />
      </template>

      <!-- ===== DEV SET ===== -->
      <template v-else-if="m.kind === 'logo'">
        <circle cx="12" cy="12" r="9" />
        <path d="M8 12l3-3" />
        <path d="M8 12l3 3" />
        <path d="M13 9l2 6" />
      </template>

      <template v-else-if="m.kind === 'code'">
        <path d="M8 8l-4 4 4 4" />
        <path d="M16 8l4 4-4 4" />
        <path d="M12 7l-2 10" />
      </template>

      <template v-else-if="m.kind === 'terminal'">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M6 9l3 3-3 3" />
        <path d="M12 15h6" />
      </template>

      <template v-else-if="m.kind === 'braces'">
        <path d="M9 6c-2 0-3 1-3 3v1c0 1-1 2-2 2 1 0 2 1 2 2v1c0 2 1 3 3 3" />
        <path d="M15 6c2 0 3 1 3 3v1c0 1 1 2 2 2-1 0-2 1-2 2v1c0 2-1 3-3 3" />
      </template>

      <template v-else-if="m.kind === 'gear'">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M16.9 16.9l2.2 2.2M2 12h3M19 12h3M4.9 19.1l2.1-2.1M16.9 7.1l2.2-2.2" />
      </template>

      <template v-else-if="m.kind === 'db'">
        <ellipse cx="12" cy="6" rx="7" ry="3" />
        <path d="M5 6v8c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
        <path d="M5 10c0 1.7 3.1 3 7 3s7-1.3 7-3" />
      </template>

      <template v-else-if="m.kind === 'cloud'">
        <path d="M7 16a4 4 0 1 1 .5-7.9A5 5 0 0 1 17 9a3.5 3.5 0 0 1 0 7H7z" />
      </template>

      <template v-else-if="m.kind === 'link'">
        <path d="M10 13a4 4 0 0 1 0-6l2-2a4 4 0 1 1 6 6l-1 1" />
        <path d="M14 11a4 4 0 0 1 0 6l-2 2a4 4 0 1 1-6-6l1-1" />
      </template>

      <template v-else-if="m.kind === 'cursor'">
        <path d="M5 4l10 6-6 2-2 6-2-14z" />
      </template>

      <template v-else-if="m.kind === 'bug'">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 5v2M12 19v-2M7 9l-2-2M17 9l2-2M7 15l-2 2M17 15l2 2M5 12h14" />
      </template>

      <!-- Nouveaux -->
      <template v-else-if="m.kind === 'api'">
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M6 12h12" />
        <path d="M7 9h2M11 9h2M15 9h2" />
      </template>

      <template v-else-if="m.kind === 'ci'">
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M7 9h10M7 13h6" />
      </template>

      <template v-else-if="m.kind === 'docker'">
        <rect x="5" y="10" width="3" height="3" />
        <rect x="9" y="10" width="3" height="3" />
        <rect x="13" y="10" width="3" height="3" />
        <rect x="9" y="6" width="3" height="3" />
        <path d="M4 15c2 2 12 2 14-2" />
      </template>

      <template v-else-if="m.kind === 'k8s'">
        <polygon points="12,3 19,8 16,19 8,19 5,8" />
        <circle cx="12" cy="12" r="2.5" />
      </template>

      <template v-else-if="m.kind === 'git'">
        <path d="M4 12l8-8 8 8-8 8-8-8z" />
        <circle cx="12" cy="12" r="2" />
      </template>

      <template v-else-if="m.kind === 'branch'">
        <circle cx="6" cy="6" r="2" />
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="6" r="2" />
        <path d="M8 6h8M6 8v8c0 4 8 4 8 0V8" />
      </template>

      <template v-else-if="m.kind === 'merge'">
        <circle cx="6" cy="6" r="2" />
        <path d="M8 6c6 0 6 6 6 12" />
        <circle cx="18" cy="18" r="2" />
      </template>

      <template v-else-if="m.kind === 'function'">
        <path d="M8 7h6M8 17h6M10 7c-2 6 2 4 1 10" />
      </template>

      <template v-else-if="m.kind === 'lambda'">
        <path d="M8 5l8 14M14 13h4" />
      </template>

      <template v-else-if="m.kind === 'brackets'">
        <path d="M9 5H6v14h3M15 5h3v14h-3" />
      </template>

      <template v-else-if="m.kind === 'bracketsSquare'">
        <path d="M9 5H6v14h3M15 5h3v14h-3" />
      </template>

      <template v-else-if="m.kind === 'bracketsAngle'">
        <path d="M8 6l-4 6 4 6M16 6l4 6-4 6" />
      </template>

      <template v-else-if="m.kind === 'fileCode'">
        <path d="M8 4h7l3 3v13H8z" />
        <path d="M15 4v4h4" />
        <path d="M10 12l-2 2 2 2M14 12l2 2-2 2" />
      </template>

      <template v-else-if="m.kind === 'server'">
        <rect x="4" y="5" width="16" height="6" rx="2" />
        <rect x="4" y="13" width="16" height="6" rx="2" />
        <path d="M7 8h2M7 16h2" />
      </template>

      <template v-else-if="m.kind === 'cloudUp'">
        <path d="M7 16a4 4 0 1 1 .5-7.9A5 5 0 0 1 17 9a3.5 3.5 0 0 1 0 7H7z" />
        <path d="M12 14v-5M9.5 11.5 12 9l2.5 2.5" />
      </template>

      <template v-else-if="m.kind === 'cloudDown'">
        <path d="M7 16a4 4 0 1 1 .5-7.9A5 5 0 0 1 17 9a3.5 3.5 0 0 1 0 7H7z" />
        <path d="M12 10v5M9.5 12.5 12 15l2.5-2.5" />
      </template>

      <template v-else-if="m.kind === 'mobile'">
        <rect x="8" y="4" width="8" height="16" rx="2" />
        <circle cx="12" cy="17" r="1" />
      </template>

      <template v-else-if="m.kind === 'desktop'">
        <rect x="3" y="5" width="18" height="12" rx="2" />
        <path d="M8 19h8M10 17h4" />
      </template>

      <template v-else-if="m.kind === 'shield'">
        <path d="M12 3l7 4v5c0 5-7 9-7 9s-7-4-7-9V7z" />
      </template>

      <template v-else-if="m.kind === 'key'">
        <circle cx="8" cy="12" r="3" />
        <path d="M11 12h9M17 10v4" />
      </template>

      <template v-else-if="m.kind === 'lock'">
        <rect x="6" y="11" width="12" height="8" rx="2" />
        <path d="M9 11V8a3 3 0 0 1 6 0v3" />
      </template>

      <template v-else-if="m.kind === 'rocket'">
        <path d="M12 2c4 2 5 7 4 10l-6 6c-3 1-8 0-10-4 4-2 6-3 7-6 1-3 2-5 5-6z" />
        <circle cx="13" cy="9" r="2" />
      </template>

      <template v-else-if="m.kind === 'beaker'">
        <path d="M6 3h12M9 3v5l-4 8h14l-4-8V3" />
      </template>

      <template v-else-if="m.kind === 'flask'">
        <path d="M10 2v5l-4 8h12l-4-8V2" />
      </template>

      <template v-else-if="m.kind === 'wrench'">
        <path d="M21 7a5 5 0 0 1-7 7l-7 7-3-3 7-7a5 5 0 0 1 7-7z" />
      </template>

      <template v-else-if="m.kind === 'lightning'">
        <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
      </template>

      <template v-else-if="m.kind === 'react'">
        <circle cx="12" cy="12" r="2" />
        <ellipse cx="12" cy="12" rx="9" ry="3" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="9" ry="3" transform="rotate(120 12 12)" />
        <ellipse cx="12" cy="12" rx="9" ry="3" />
      </template>

      <template v-else-if="m.kind === 'vue'">
        <path d="M3 4l9 16L21 4h-4l-5 9L7 4z" />
      </template>

      <template v-else-if="m.kind === 'nodejs'">
        <polygon points="12,2 21,7 21,17 12,22 3,17 3,7" />
        <path d="M8 10v4c0 2 4 2 4 0v-4" />
      </template>

      <template v-else-if="m.kind === 'python'">
        <path d="M8 7a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3v3H9a3 3 0 0 0-3 3v1" />
        <path d="M16 17a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3v-3h7a3 3 0 0 0 3-3v-1" />
        <circle cx="10" cy="6" r="0.8" />
        <circle cx="14" cy="18" r="0.8" />
      </template>

      <template v-else-if="m.kind === 'js'">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M9 8v6a2 2 0 0 0 2 2M15 8a3 3 0 0 1 0 6h-2" />
      </template>

      <template v-else-if="m.kind === 'ts'">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M8 9h8M12 9v8M9 13h3" />
      </template>

      <!-- Fallback -->
      <template v-else>
        <circle cx="12" cy="12" r="2" />
      </template>
    </svg>


  </div>
</template>

<script>
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// liste des motifs réellement supportés par le template (sécurité)
const SUPPORTED_KINDS = new Set([
  // Mail set
  'mail','plane','chat','at','dot',
  // Dev set
  'logo','code','terminal','braces','gear','db','cloud','link','cursor','bug',
  'api','ci','docker','k8s','git','branch','merge',
  'function','lambda','brackets','bracketsSquare','bracketsAngle',
  'fileCode','server','cloudUp','cloudDown','mobile','desktop',
  'shield','key','lock','rocket','beaker','flask','wrench','lightning',
  'react','vue','nodejs','python','js','ts'
]);

export default {
  name: 'MotifBackdrop',
  props: {
    /* calques */
    showGrid: { type: Boolean, default: false },
    gridOpacity: { type: Number, default: 0.05 },
    showTiledPattern: { type: Boolean, default: true },
    patternOpacity: { type: Number, default: 0.06 },
    showPaperField: { type: Boolean, default: true },
    paperOpacity: { type: Number, default: 0.10 },
    tileSize: { type: Number, default: 140 },
    showHalos: { type: Boolean, default: true },

    /* population & style */
    randomIcons: { type: Boolean, default: true },
    randomDensity: { type: Number, default: 1.0 },
    sizeMin: { type: Number, default: 26 },
    sizeMax: { type: Number, default: 64 },
    strokeMin: { type: Number, default: 1.2 },
    strokeMax: { type: Number, default: 1.8 },
    opacityMin: { type: Number, default: 0.06 },
    opacityMax: { type: Number, default: 0.35 },
    rotMin: { type: Number, default: -25 },
    rotMax: { type: Number, default: 25 },
    marginX: { type: Number, default: 1.5 }, // marge (%)
    marginY: { type: Number, default: 4 },

    /* contrôle du set & pondération (fallback général) */
    kinds: {
      type: Array,
      default: () => ([
        'mail','plane','chat','at','dot',
        'logo','code','terminal','braces','gear','db','cloud','link','cursor','bug',
        'api','ci','docker','k8s','git','branch','merge',
        'function','lambda','brackets','bracketsSquare','bracketsAngle',
        'fileCode','server','cloudUp','cloudDown','mobile','desktop',
        'shield','key','lock','rocket','beaker','flask','wrench','lightning',
        'react','vue','nodejs','python','js','ts'
      ])
    },

    /** ✅ NOUVEAU : si fourni, *seuls* ces motifs seront utilisés */
    allowedKinds: {
      type: Array,
      default: null // null = non fourni => on retombe sur 'kinds'
    },

    /** ex: { logo: 2, code: 1, mail: 0.5 } */
    weights: {
      type: Object,
      default: () => ({})
    },

    /* seed pour résultat stable */
    seed: { type: [Number, String], default: null },

    /* palette de couleurs */
    colorPalette: {
      type: Array,
      default: () => ([
        'rgba(47,111,255,0.35)',   // brand-500
        'rgba(30,58,138,0.30)',    // brand-800
        'rgba(88,101,242,0.30)',   // indigo-ish
        'rgba(17,24,39,0.20)'      // ink-ish
      ])
    }
  },
  data() {
    return { motifs: [] };
  },
  mounted() { this.generate(); },
  watch: {
    randomIcons() { this.generate(); }, randomDensity() { this.generate(); },
    sizeMin() { this.generate(); }, sizeMax() { this.generate(); },
    strokeMin() { this.generate(); }, strokeMax() { this.generate(); },
    opacityMin() { this.generate(); }, opacityMax() { this.generate(); },
    rotMin() { this.generate(); }, rotMax() { this.generate(); },
    marginX() { this.generate(); }, marginY() { this.generate(); },
    kinds() { this.generate(); },
    allowedKinds: { handler() { this.generate(); }, deep: true }, // ✅ watcher
    weights: { handler() { this.generate(); }, deep: true },
    seed() { this.generate(); }, colorPalette() { this.generate(); }
  },
  methods: {
    generate() {
      if (!this.randomIcons) { this.motifs = []; return; }

      // 1) Choisir la source de vérité :
      //    - si allowedKinds est fourni (même []) => on l'utilise tel quel (après sanitation)
      //    - sinon on utilise kinds
      const source = Array.isArray(this.allowedKinds) ? this.allowedKinds : this.kinds;

      // 2) Sanitize: garder uniquement les motifs supportés
      let effectiveKinds = (source || []).filter(k => SUPPORTED_KINDS.has(k));

      // 3) Si allowedKinds a été fourni ET est vide => aucun motif
      if (Array.isArray(this.allowedKinds) && this.allowedKinds.length === 0) {
        this.motifs = [];
        return;
      }

      // 4) Si rien après filtrage, retomber sur un point discret pour éviter écran vide involontaire
      if (!effectiveKinds.length) effectiveKinds = ['dot'];

      // 5) Base & densité
      const base = 120;
      const count = Math.max(0, Math.round(base * this.randomDensity));

      // 6) RNG seedable
      let rand = Math.random;
      if (this.seed !== null && this.seed !== '') {
        const s = Number(String(this.seed).split('').reduce((a, c) => a + c.charCodeAt(0), 0));
        rand = mulberry32(s);
      }
      const rnd  = (a, b) => rand() * (b - a) + a;
      const pick = (arr) => arr[Math.floor(rand() * arr.length)];

      // 7) Pondération : n’appliquer les weights QUE sur les effectiveKinds
      const expandedKinds = [];
      for (const k of effectiveKinds) {
        const w = Number(this.weights[k] ?? 1);
        if (w > 0) {
          const copies = Math.max(1, Math.round(w * 3)); // amplification simple (3 = facteur confortable)
          for (let i = 0; i < copies; i++) expandedKinds.push(k);
        }
      }

      const pool = expandedKinds.length ? expandedKinds : effectiveKinds;

      // 8) Génération des motifs
      this.motifs = Array.from({ length: count }).map((_, i) => ({
        id: i,
        x: rnd(this.marginX, 100 - this.marginX),
        y: rnd(this.marginY, 100 - this.marginY),
        size: rnd(this.sizeMin, this.sizeMax),
        rot: rnd(this.rotMin, this.rotMax),
        kind: pick(pool),
        color: pick(this.colorPalette),
        stroke: rnd(this.strokeMin, this.strokeMax),
        opacity: rnd(this.opacityMin, this.opacityMax)
      }));
    }
  }
};
</script>

<style scoped>
/* grille douce */
.bg-grid {
  background-image:
    linear-gradient(to right, rgba(16, 24, 40, 0.06) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(16, 24, 40, 0.06) 1px, transparent 1px);
  background-size: 36px 36px, 36px 36px;
  background-position: center;
}

/* motif tuile */
.bg-mail-pattern {
  --mailOpacity: .06;
  opacity: var(--mailOpacity);
  filter: saturate(1.05);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 120 120' fill='none' stroke='rgb(30,58,138)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='12' y='16' width='28' height='20' rx='3'/%3E%3Cpath d='M12 19l14 11 14-11'/%3E%3Cpath d='M70 18l36 14-20 6-16-20z'/%3E%3Cpath d='M86 38l-4 10'/%3E%3Crect x='14' y='68' width='34' height='24' rx='6'/%3E%3C/svg%3E");
  background-size: var(--tile, 140px) var(--tile, 140px);
  background-repeat: repeat;
  background-position: center;
}

/* field “papier” + vignette douce */
.mail-field {
  --mailOpacity: .10;
  opacity: var(--mailOpacity);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 120 120' fill='none' stroke='rgb(30,58,138)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='12' y='16' width='28' height='20' rx='3'/%3E%3Cpath d='M12 19l14 11 14-11'/%3E%3Cpath d='M70 18l36 14-20 6-16-20z'/%3E%3Cpath d='M86 38l-4 10'/%3E%3Crect x='14' y='68' width='34' height='24' rx='6'/%3E%3C/svg%3E");
  background-size: var(--tile, 140px) var(--tile, 140px);
  background-repeat: repeat;
  background-position: center;
  -webkit-mask-image: radial-gradient(120% 120% at 50% 40%, #000 70%, transparent 100%);
          mask-image: radial-gradient(120% 120% at 50% 40%, #000 70%, transparent 100%);
}

/* dark mode léger */
:root.dark .bg-mail-pattern { opacity:.045; }
:root.dark .mail-field { opacity:.07; }
</style>